import type { RecallItem } from '$domain/types';
import type { ForecastDay, ForecastStats } from './types';

export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function dayKeyFromIso(iso: string): string {
  return dayKey(new Date(iso));
}

export function endOfDay(d: Date): Date {
  const end = new Date(d);
  end.setHours(23, 59, 59, 999);
  return end;
}

export function heatLevel(count: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (count <= 0 || max <= 0) return 0;
  const ratio = count / max;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

export function computeForecast(
  items: RecallItem[],
  days: number = 30,
  now: Date = new Date()
): ForecastDay[] {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const todayKey = dayKey(start);

  const buckets: ForecastDay[] = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    buckets.push({
      date: dayKey(d),
      timestamp: d.getTime(),
      count: 0,
      heatLevel: 0,
      isToday: i === 0
    });
  }

  for (const item of items) {
    const itemKey = dayKeyFromIso(item.nextReviewAt);
    if (itemKey <= todayKey) {
      buckets[0]!.count += 1;
      continue;
    }
    const idx = buckets.findIndex((b) => b.date === itemKey);
    if (idx >= 0) buckets[idx]!.count += 1;
  }

  const max = buckets.reduce((m, b) => (b.count > m ? b.count : m), 0);
  for (const b of buckets) {
    b.heatLevel = heatLevel(b.count, max);
  }
  return buckets;
}

export function computeStats(
  items: RecallItem[],
  now: Date = new Date()
): ForecastStats {
  const cutoff = endOfDay(now).getTime();
  let dueToday = 0;
  let sumStability = 0;
  let sumDifficulty = 0;
  for (const item of items) {
    if (new Date(item.nextReviewAt).getTime() <= cutoff) dueToday += 1;
    sumStability += item.stability;
    sumDifficulty += item.difficulty;
  }
  const n = items.length;
  return {
    dueToday,
    active: n,
    avgStability: n > 0 ? sumStability / n : 0,
    avgDifficulty: n > 0 ? sumDifficulty / n : 0
  };
}